import { createPortal } from 'react-dom'
import { KTSVG } from '_metronic/helpers'
import { Modal } from 'react-bootstrap'
import { Lightbox } from 'react-modal-image'

import useViewModel from './ViewModel'
import clsx from 'clsx'
import { Fragment } from 'react'

interface Props {
  variant?: 'modal' | 'inline'
  category?: 'general' | 'sales'
}

const AttachmentsPanel: React.FC<{
  isView: boolean
  isLoading: boolean
  attachImage: any[]
  addImage: (files: FileList) => void
  removeImage: (idx: number) => void
  openImage: (idx: number) => void
  onChangeNote: (idx: number, note: string) => void
  saveNote: (idx: number) => void
  downloadFile: (idx: number) => void
  openLightBox: boolean
  lightBoxImage: string
  lightBoxAlt: string
  closeImage: () => void
  inputId?: string
}> = ({
  isView,
  isLoading,
  attachImage,
  addImage,
  removeImage,
  openImage,
  onChangeNote,
  saveNote,
  downloadFile,
  openLightBox,
  lightBoxImage,
  lightBoxAlt,
  closeImage,
  inputId = 'attach-file-image-input',
}) => (
  <>
    {!isView && (
      <Fragment>
        <div
          className={clsx('border-dashed w-100 h-150px bg-hover-light transition-500', {
            'opacity-50': isLoading,
          })}
          onClick={() => {
            if (!isLoading) {
              document.getElementById(inputId)?.click()
            }
          }}>
          <div className='d-flex flex-column align-items-center justify-content-center h-100 cursor-pointer'>
            {isLoading ? (
              <div className='d-flex flex-column align-items-center'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <p className='text-gray-500 fw-bold fs-3 mb-5 mt-5'>Uploading...</p>
              </div>
            ) : (
              <>
                <KTSVG
                  className='svg-icon-5x svg-icon-primary'
                  path='/media/icons/duotune/general/gen006.svg'
                />
                <p className='text-gray-500 fw-bold fs-3 mb-5 mt-5'>Browse Files</p>
                <p className='text-gray-400'>Supports: PNG, JPG, JPEG, PDF, XLSX, XLS, DOCX</p>
              </>
            )}

            <input
              id={inputId}
              type='file'
              className='d-none'
              multiple
              disabled={isLoading}
              onChange={(e: any) => {
                addImage(e.target.files as FileList)
                e.target.value = ''
              }}
              accept='.png, .jpeg, .jpg, .pdf, .xlsx, .xls, .docx'
            />
          </div>
        </div>
      </Fragment>
    )}

    {attachImage.map((image, idx) => (
      <div
        key={`image-${idx}-${image.fileName}`}
        className='row align-items-center bg-grey border rounded-2 my-5 shadow-sm p-3'>
        <div className='col-12 col-md-6 col-lg-8 d-flex align-items-center'>
          <div className='text-center' style={{ minWidth: '200px' }}>
            {['.png', '.jpeg', '.jpg', '.gif'].some(ft =>
              image.fileName.toLowerCase().includes(ft)
            ) && (
              <img
                src={image.imgPath}
                alt={image.fileName}
                className='img-fluid rounded shadow-sm cursor-pointer'
                style={{ maxHeight: '100px', width: 'auto' }}
                onClick={() => openImage(idx)}
              />
            )}
            {['.png', '.jpeg', '.jpg', '.gif'].every(
              ft => !image.fileName.toLowerCase().includes(ft)
            ) && <div>{image.fileName}</div>}
          </div>
          <input
            className='form-control form-control-sm mt-2 ms-3'
            placeholder='ระบุหมายเหตุ'
            value={image.description}
            onChange={(e: any) => onChangeNote(idx, e.target.value)}
            disabled={isView}
          />
        </div>

        <div className='col-12 col-md-6 col-lg-4 d-flex justify-content-end mt-3 mt-md-0'>
          {['.png', '.jpeg', '.jpg', '.gif'].every(
            ft => !image.fileName.toLowerCase().includes(ft)
          ) && (
            <button
              type='button'
              className='btn btn-sm btn-icon btn-success btn-active-light-success me-3'
              onClick={() => downloadFile(idx)}>
              <KTSVG className='svg-icon-1' path='/media/icons/duotune/files/fil021.svg' />
            </button>
          )}
          {!isView && (
            <button
              className={clsx('btn btn-sm btn-icon me-3', {
                'btn-light-primary btn-active-primary': image.isEditNote,
                'btn-secondary': !image.isEditNote,
              })}
              disabled={!image.isEditNote}
              onClick={() => saveNote(idx)}>
              <KTSVG className='svg-icon-1' path='/media/icons/duotune/general/gen037.svg' />
            </button>
          )}
          {['.png', '.jpeg', '.jpg', '.gif'].some(ft =>
            image.fileName.toLowerCase().includes(ft)
          ) && (
            <button
              className='btn btn-sm btn-icon btn-light-success btn-active-success me-3'
              onClick={() => openImage(idx)}>
              <KTSVG className='svg-icon-1' path='/media/icons/duotune/files/fil024.svg' />
            </button>
          )}

          {!isView && (
            <button
              className='btn btn-sm btn-icon btn-light-danger btn-active-danger'
              onClick={() => removeImage(idx)}>
              <KTSVG className='svg-icon-1' path='/media/icons/duotune/files/fil007.svg' />
            </button>
          )}
        </div>
      </div>
    ))}

    {openLightBox && (
      <Lightbox
        medium={lightBoxImage}
        large={lightBoxImage}
        alt={lightBoxAlt}
        onClose={closeImage}
      />
    )}
  </>
)

const ImageUploader: React.FC<Props> = ({ variant = 'modal', category = 'general' }) => {
  const {
    isView,
    open,
    handleClose,
    addImage,
    removeImage,
    attachImage,
    lightBoxImage,
    lightBoxAlt,
    openImage,
    closeImage,
    openLightBox,
    onChangeNote,
    saveNote,
    isLoading,
    downloadFile,
  } = useViewModel({ variant, category })

  const panelProps = {
    isView,
    isLoading,
    attachImage,
    addImage,
    removeImage,
    openImage,
    onChangeNote,
    saveNote,
    downloadFile,
    openLightBox,
    lightBoxImage,
    lightBoxAlt,
    closeImage,
    inputId: `attach-file-image-input-${category}-${variant}`,
  }

  if (variant === 'inline') {
    return (
      <div className='min-h-300px'>
        <AttachmentsPanel {...panelProps} />
      </div>
    )
  }

  const modalsRoot = document.getElementById('root-modals') || document.body

  return createPortal(
    <Modal
      id='kt_modal_project_import'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-900px'
      show={open}
      onHide={handleClose}
      backdrop={true}>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2>ดู/แนบไฟล์เพิ่มเติม</h2>
          <div className='btn btn-sm btn-icon btn-active-light-danger' onClick={handleClose}>
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/general/gen040.svg' />
          </div>
        </div>

        <div className='modal-body py-lg-10 px-lg-10 min-h-500px'>
          <AttachmentsPanel {...panelProps} />
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export default ImageUploader
